import React from "react";
import api from "@/lib/api";
import en from "@/locales/en.json";
import es from "@/locales/es.json";

export const LANGS = [["it", "Italiano"], ["en", "English"], ["es", "Español"]];
const idx = (d) => Object.fromEntries(Object.entries(d).map(([k, v]) => [norm(k), v]));
const DICTS = { en: idx(en), es: idx(es) };
const cur = () => DICTS[getLang()] || {};
const SKIP = new Set(["SCRIPT", "STYLE", "TEXTAREA", "INPUT", "CODE", "PRE"]);
const ATTRS = ["placeholder", "title", "aria-label"];

function norm(s) { return s.replace(/\s+/g, " ").trim(); }

export function getLang() {
  const l = localStorage.getItem("lang");
  return l === "en" || l === "es" ? l : "it";
}

export function t(s) {
  if (getLang() === "it" || !s) return s;
  return cur()[norm(s)] ?? s;
}

export function useT() { return t; }

export async function setLang(lang, loggedIn) {
  localStorage.setItem("lang", lang);
  if (loggedIn) {
    try { await api.patch("/auth/me/language", { language: lang }); } catch { /* preferenza locale comunque salvata */ }
  }
  window.location.reload();
}

// Allinea la lingua salvata sull'account con quella del browser (ricarica una sola volta)
export function syncUserLang(user) {
  if (user?.language && user.language !== getLang()) {
    localStorage.setItem("lang", user.language);
    window.location.reload();
  }
}

function translateText(node) {
  const v = node.nodeValue;
  if (!v || !v.trim()) return;
  const tr = cur()[norm(v)];
  if (tr && tr !== norm(v)) {
    const lead = v.match(/^\s*/)[0], trail = v.match(/\s*$/)[0];
    node.nodeValue = lead + tr + trail;
  }
}

function skipped(el) {
  return !el || el.closest?.("[data-no-i18n],[contenteditable=true]") || SKIP.has(el.tagName);
}

function translateTree(root) {
  if (root.nodeType === 3) { if (!skipped(root.parentElement)) translateText(root); return; }
  if (root.nodeType !== 1 || skipped(root)) return;
  ATTRS.forEach((a) => {
    root.querySelectorAll?.(`[${a}]`).forEach((el) => {
      const tr = cur()[norm(el.getAttribute(a) || "")];
      if (tr) el.setAttribute(a, tr);
    });
    const own = root.getAttribute?.(a);
    if (own && cur()[norm(own)]) root.setAttribute(a, cur()[norm(own)]);
  });
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let n;
  while ((n = w.nextNode())) { if (!skipped(n.parentElement)) translateText(n); }
}

export function startAutoTranslate() {
  document.documentElement.lang = getLang();
  if (getLang() === "it") return;
  translateTree(document.body);
  new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === "characterData") { if (!skipped(m.target.parentElement)) translateText(m.target); }
      else if (m.type === "attributes") { translateTree(m.target); }
      else m.addedNodes.forEach(translateTree);
    }
  }).observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
}

export function LanguageSwitch({ loggedIn, dark, withEs = false }) {
  const cur = getLang();
  const langs = LANGS.filter(([k]) => k !== "es" || withEs || cur === "es");
  return (
    <div className={`inline-flex rounded-full p-0.5 text-[11px] border ${dark ? "border-white/20" : "border-stone-200 bg-white"}`} data-testid="language-switch" data-no-i18n>
      {langs.map(([k, label]) => (
        <button key={k} type="button" onClick={() => k !== cur && setLang(k, loggedIn)} title={label} data-testid={`language-${k}`}
          className={`px-2.5 py-0.5 rounded-full uppercase tracking-wider transition-colors ${k === cur ? (dark ? "bg-white text-[#1C352D]" : "bg-[#1C352D] text-white") : (dark ? "text-white/70 hover:text-white" : "text-stone-500 hover:text-stone-800")}`}>
          {k}
        </button>
      ))}
    </div>
  );
}
